import { Controller } from '@hotwired/stimulus'
import scrollTick from '../utils/scroll_tick'

// Drives a stack of sticky cards: as the page scrolls, each card parks under
// the navbar and the next one slides up over it.
//
//   <div data-controller="scroll-deck">
//     <div data-scroll-deck-target="head">...</div>      (optional)
//     <ol data-scroll-deck-target="list">
//       <li data-scroll-deck-target="card" style="--i: 0">...</li>
//       <li data-scroll-deck-target="card" style="--i: 1">...</li>
//     </ol>
//   </div>
//
// With a `head`, its height (plus bottom margin) is written to `--deck-head`
// on the controller element so CSS can pin the heading and park the cards
// beneath it, and `--deck-tail` is how far above the element's end the head's
// own sticky box must stop so the head is pushed off the moment the last card
// is — the two share no containing block otherwise.
//
// The last card that has reached its sticky offset is `is-active`; every card
// before it is `is-behind` with `--depth` set to how many cards sit on top of
// it. CSS owns the rest: the active card's animation starts from zero when the
// class lands, a behind card keeps its finished state, and a card that has not
// parked yet (or that the reader scrolled back above) carries no animation at
// all, so it replays the next time it parks.
//
// Clicking (or pressing Enter/Space on) any card that is not active scrolls the
// page to the exact position where that card parks, so the stack edges double
// as step navigation.
// Set on <html> while the deck is on screen; CSS turns root snapping on for it.
const SNAPPING_CLASS = 'uberv2-snapping'

export default class extends Controller {
  static targets = ['card', 'list', 'head']

  connect() {
    this.stopTicking = scrollTick(this.element, {
      tick: () => this.tick(),
      stop: () => document.documentElement.classList.remove(SNAPPING_CLASS)
    })
  }

  disconnect() {
    this.stopTicking()
  }

  select(event) {
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return
    if (event.target.closest('a, button')) return

    const card = event.currentTarget
    if (card.classList.contains('is-active')) return
    event.preventDefault()

    window.scrollTo({ top: this.parkedScrollTop(card), behavior: 'smooth' })
  }

  // The document scroll position at which `card` has just reached its sticky
  // offset. Sticky boxes report their stuck position, so the in-flow top is
  // rebuilt from the deck's top plus every card and gap above this one.
  parkedScrollTop(card) {
    const list = this.listTarget
    let flowTop = list.getBoundingClientRect().top + window.scrollY
    flowTop += parseFloat(getComputedStyle(list).paddingTop) || 0
    for (const sibling of this.cardTargets) {
      flowTop += parseFloat(getComputedStyle(sibling).marginTop) || 0
      if (sibling === card) break
      // Layout height, not the bounding rect: a behind card is scaled down.
      flowTop += sibling.offsetHeight
    }
    const stickyTop = parseFloat(getComputedStyle(card).top) || 0
    return flowTop - stickyTop
  }

  // Every layout read happens before any write: the custom properties written
  // here feed the cards' sticky offsets, so a read after a write would force
  // a synchronous layout.
  tick() {
    const cards = this.cardTargets.map((card) => ({
      card,
      top: card.getBoundingClientRect().top,
      stickyTop: parseFloat(getComputedStyle(card).top) || 0,
      height: card.offsetHeight
    }))
    const head = this.hasHeadTarget ? this.measureHead(cards[cards.length - 1]) : null
    const snapping = this.deckOnScreen(cards)

    if (head) {
      this.element.style.setProperty('--deck-head', `${head.height}px`)
      if (head.tail !== null) this.element.style.setProperty('--deck-tail', `${head.tail}px`)
    }

    let active = 0
    cards.forEach(({ top, stickyTop, height }, index) => {
      // A card takes over while it is still sliding up, once it has covered
      // about half of the card it is landing on — waiting for it to park fully
      // reads as lag. The lead is capped so a tall card on a short viewport
      // does not go live before it is really on screen.
      const lead = Math.min(height * 0.5, window.innerHeight * 0.35)
      if (top <= stickyTop + lead) active = index
    })

    cards.forEach(({ card }, index) => {
      card.classList.toggle('is-active', index === active)
      card.classList.toggle('is-behind', index < active)
      card.style.setProperty('--depth', Math.max(active - index, 0))
    })

    document.documentElement.classList.toggle(SNAPPING_CLASS, snapping)
  }

  // The pinned head's height (plus its bottom margin) pushes the cards down;
  // `tail` is how far the last card hangs below the head once parked.
  measureHead(last) {
    const head = this.headTarget
    const gap = parseFloat(getComputedStyle(head).marginBottom) || 0
    let tail = null
    if (last) {
      const headTop = parseFloat(getComputedStyle(head).top) || 0
      tail = Math.max(last.height + (last.stickyTop - headTop) - head.offsetHeight, 0)
    }
    return { height: head.offsetHeight + gap, tail }
  }

  // Root scroll snapping is on only while the deck is in play: from the first
  // card entering the lower half of the viewport until the last card starts
  // to leave its parked position. Outside that band the page scrolls freely.
  deckOnScreen(cards) {
    if (cards.length === 0) return false
    const first = cards[0]
    const last = cards[cards.length - 1]
    const listBottom = this.listTarget.getBoundingClientRect().bottom
    return first.top < window.innerHeight * 0.5 &&
      listBottom > last.stickyTop + last.height + 1
  }
}
